// components/student/StudentHeader.js
import React, { useState, useRef, useEffect } from "react";
import { Navbar, Container, Form, InputGroup } from "react-bootstrap";
import "../../../assets/css/student/Header.css";

/* ── SVG Icons ── */
const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.25s ease" }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/* ── Static Data ── */
const NOTIFICATIONS = [
  { id: 1, type: "assignment", title: "New Assignment Posted",  message: "JavaScript Arrays assignment is now available",  time: "5 min ago",   unread: true,  color: "#6366f1" },
  { id: 2, type: "grade",      title: "Assignment Graded",      message: "Your React Hooks assignment has been graded",    time: "1 hour ago",  unread: true,  color: "#10b981" },
  { id: 3, type: "announcement",title: "Course Update",         message: "New resources added to Web Development",         time: "3 hours ago", unread: false, color: "#f59e0b" },
  { id: 4, type: "reminder",   title: "Deadline Reminder",      message: "CSS Grid assignment due tomorrow",               time: "5 hours ago", unread: false, color: "#ef4444" },
];

const CHATS = [
  { id: 1, name: "Dr. Sarah Ahmed",  message: "Great work on your last assignment!", time: "10 min ago",  initials: "SA", color: "#6366f1", unread: true,  online: true  },
  { id: 2, name: "Prof. Hassan Ali", message: "Can you clarify your question…",      time: "1 hour ago",  initials: "HA", color: "#10b981", unread: true,  online: false },
  { id: 3, name: "Study Group",      message: "Meeting at 3 PM today",               time: "2 hours ago", initials: "SG", color: "#f59e0b", unread: false, online: true  },
  { id: 4, name: "Ms. Fatima Khan",  message: "Thanks for your response",            time: "1 day ago",   initials: "FK", color: "#ec4899", unread: false, online: false },
];

const TYPE_EMOJI = {
  assignment:   "📝",
  grade:        "⭐",
  announcement: "📢",
  reminder:     "⏰",
};

/* ════════════════════════════════════════
   HEADER COMPONENT
   ════════════════════════════════════════ */
function Header({ studentName, studentAvatar, studentEmail }) {
  const [showNotif,   setShowNotif]   = useState(false);
  const [showChat,    setShowChat]    = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchVal,   setSearchVal]   = useState("");
  const ref = useRef(null);

  const unreadNotif = NOTIFICATIONS.filter((n) => n.unread).length;
  const unreadChat  = CHATS.filter((c) => c.unread).length;
  const avatarLetter = studentName ? studentName[0].toUpperCase() : "S";

  /* Close panels on outside click */
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShowNotif(false);
        setShowChat(false);
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (p) => {
    setShowNotif  (p === "notif"   ? !showNotif   : false);
    setShowChat   (p === "chat"    ? !showChat    : false);
    setShowProfile(p === "profile" ? !showProfile : false);
  };

  return (
    <Navbar className="tmh-header" ref={ref}>
      <Container fluid className="tmh-container">

        {/* ── BRAND ── */}
        <Navbar.Brand className="tmh-brand" href="#">
          <div className="tmh-logo-mark">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" fill="white" />
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="tmh-brand-words">
            <span className="tmh-brand-name">TechMentor</span>
            <span className="tmh-brand-sub">Hub</span>
          </div>
        </Navbar.Brand>

        {/* ── SEARCH ── */}
        <div className="tmh-search-wrap">
          <InputGroup className="tmh-search-group">
            <Form.Control
              className="tmh-search-input"
              placeholder="Search course, events..."
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
            />
            <InputGroup.Text className="tmh-search-addon">
              <SearchIcon />
            </InputGroup.Text>
          </InputGroup>
        </div>

        {/* ── RIGHT ACTIONS ── */}
        <div className="tmh-right">

          {/* Bell */}
          <div className="tmh-action-cell">
            <button
              className={`tmh-icon-btn ${showNotif ? "active" : ""}`}
              onClick={() => toggle("notif")}
              aria-label="Notifications"
            >
              <BellIcon />
              {unreadNotif > 0 && <span className="tmh-badge">{unreadNotif}</span>}
            </button>

            {showNotif && (
              <div className="tmh-panel">
                <div className="tmh-panel-head">
                  <div>
                    <p className="tmh-panel-title">Notifications</p>
                    <p className="tmh-panel-sub">{unreadNotif} unread</p>
                  </div>
                  <button className="tmh-text-btn">Mark all read</button>
                </div>
                <div className="tmh-panel-body">
                  {NOTIFICATIONS.map((n) => (
                    <div key={n.id} className={`tmh-notif-row ${n.unread ? "is-unread" : ""}`}>
                      <div className="tmh-notif-ico" style={{ background: `${n.color}1a`, color: n.color }}>
                        {TYPE_EMOJI[n.type]}
                      </div>
                      <div className="tmh-notif-text">
                        <span className="tmh-notif-title">{n.title}</span>
                        <span className="tmh-notif-msg">{n.message}</span>
                        <span className="tmh-notif-time">{n.time}</span>
                      </div>
                      {n.unread && <span className="tmh-dot" style={{ background: n.color }} />}
                    </div>
                  ))}
                </div>
                <div className="tmh-panel-foot">
                  <button className="tmh-foot-btn">View all notifications →</button>
                </div>
              </div>
            )}
          </div>

          {/* Chat */}
          <div className="tmh-action-cell">
            <button
              className={`tmh-icon-btn ${showChat ? "active" : ""}`}
              onClick={() => toggle("chat")}
              aria-label="Messages"
            >
              <ChatIcon />
              {unreadChat > 0 && <span className="tmh-badge">{unreadChat}</span>}
            </button>

            {showChat && (
              <div className="tmh-panel">
                <div className="tmh-panel-head">
                  <div>
                    <p className="tmh-panel-title">Messages</p>
                    <p className="tmh-panel-sub">{unreadChat} unread</p>
                  </div>
                  <button className="tmh-compose-btn">+ New</button>
                </div>
                <div className="tmh-panel-body">
                  {CHATS.map((c) => (
                    <div key={c.id} className={`tmh-chat-row ${c.unread ? "is-unread" : ""}`}>
                      <div className="tmh-chat-avi" style={{ background: c.color }}>
                        {c.initials}
                        {c.online && <span className="tmh-online-dot" />}
                      </div>
                      <div className="tmh-chat-text">
                        <span className="tmh-chat-name">{c.name}</span>
                        <span className="tmh-chat-msg">{c.message}</span>
                        <span className="tmh-chat-time">{c.time}</span>
                      </div>
                      {c.unread && <span className="tmh-dot" style={{ background: c.color }} />}
                    </div>
                  ))}
                </div>
                <div className="tmh-panel-foot">
                  <button className="tmh-foot-btn">View all messages →</button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="tmh-vdivider" />

          {/* Profile */}
          <div className="tmh-action-cell">
            <button
              className={`tmh-profile-btn ${showProfile ? "active" : ""}`}
              onClick={() => toggle("profile")}
            >
              <div className="tmh-avatar">
                {studentAvatar
                  ? <img src={studentAvatar} alt="avatar" className="tmh-avatar-img" />
                  : <span>{avatarLetter}</span>
                }
                <span className="tmh-avatar-status" />
              </div>
              <ChevronIcon open={showProfile} />
            </button>

            {showProfile && (
              <div className="tmh-panel tmh-profile-panel">
                <div className="tmh-profile-banner">
                  <div className="tmh-banner-avi">
                    {studentAvatar
                      ? <img src={studentAvatar} alt="avatar" className="tmh-avatar-img" />
                      : <span>{avatarLetter}</span>
                    }
                  </div>
                  <div className="tmh-banner-info">
                    {/* ── Dynamic name & email ── */}
                    <p className="tmh-banner-name">{studentName || "Student"}</p>
                    <p className="tmh-banner-email">{studentEmail || "student@example.com"}</p>
                    <span className="tmh-active-tag">
                      <span className="tmh-tag-dot" />
                      Active
                    </span>
                  </div>
                </div>

                <div className="tmh-stats">
                  {[["6", "Courses"], ["82%", "Progress"], ["A+", "Grade"]].map(([v, l], i) => (
                    <React.Fragment key={l}>
                      {i > 0 && <div className="tmh-stat-sep" />}
                      <div className="tmh-stat-item">
                        <span className="tmh-stat-val">{v}</span>
                        <span className="tmh-stat-label">{l}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                <div className="tmh-menu">
                  {[
                    ["👤", "My Profile"],
                    ["📊", "My Progress"],
                    ["⚙️",  "Settings"],
                    ["🎨", "Preferences"],
                    ["❓", "Help & Support"],
                  ].map(([icon, label]) => (
                    <button key={label} className="tmh-menu-row">
                      <span className="tmh-menu-ico">{icon}</span>
                      <span className="tmh-menu-lbl">{label}</span>
                      <svg className="tmh-menu-arrow" width="11" height="11" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  ))}
                  <div className="tmh-menu-sep" />
                  <button className="tmh-menu-row tmh-logout">
                    <span className="tmh-menu-ico">🚪</span>
                    <span className="tmh-menu-lbl">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </Navbar>
  );
}

export default Header;